import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { Button } from '@/components/ui/button';
import { useHasPermission } from '@/auth/auth-store';
import {
  CONFIG_NAMESPACES,
  useAllTerminals,
  useTerminalConfig,
  useUpdateTerminal,
  type ConfigNamespace,
  type TerminalConfigOverrideValue,
  type TerminalConfigValue,
} from '@/hooks/use-terminals';
import {
  NamespaceFields,
  NAMESPACE_DEFAULTS,
  namespaceLabel,
  summariseNamespace,
} from './terminal-config-fields';
import { PinnedParameters } from '@/pages/parameters/pinned-parameters';
import { KitchenHealthCard } from './kitchen-health-card';
import { SecureScaleCopyCard } from './securescale-copy-card';
import { CardEodPanel } from './card-eod-panel';
import { FiscalHoldPanel } from './fiscal-hold-panel';
import { TerminalPrroPanel } from '@/pages/fiscal-cabinet/fiscal-record-panels';
import { useT } from '@/i18n/use-t';

/**
 * RFL-241 — per-terminal configuration override, shown against what the
 * profile provides.
 *
 * ------------------------------------------------------------------
 * WHY THERE ARE THREE STATES AND NOT TWO
 * ------------------------------------------------------------------
 * A namespace on a terminal is one of:
 *
 *   inherited  — absent from the override; the profile's value is delivered
 *   overridden — set here; this value is delivered instead
 *   suppressed — an explicit `null`; NOTHING is delivered for this namespace
 *                and the till falls back to its own local default
 *
 * "Suppressed" is not "inherited with an empty value" and it is not
 * "overridden with nothing". It is the only way to say "this profile
 * configures cash rounding, and this one lane must not have it" — and if the
 * UI collapsed it into either neighbour, that instruction would be
 * unexpressible from Back Office while remaining perfectly expressible through
 * the API.
 *
 * The merged result is read from the server, never recomputed here.
 */

type OverrideState = 'inherited' | 'overridden' | 'suppressed';

function stateOf(override: TerminalConfigOverrideValue, ns: ConfigNamespace): OverrideState {
  if (!(ns in override)) return 'inherited';
  return override[ns] === null ? 'suppressed' : 'overridden';
}

const STATE_LABEL: Record<OverrideState, string> = {
  inherited: 'Inherited from profile',
  overridden: 'Overridden here',
  suppressed: 'Suppressed — till uses its own default',
};

/** Catalogue key per state — declared here so the parity guard can see them. */
const STATE_KEY: Record<OverrideState, string> = {
  inherited: 'terminals.detail.stateInherited',
  overridden: 'terminals.detail.stateOverridden',
  suppressed: 'terminals.detail.stateSuppressed',
};

export function TerminalDetailPage() {
  const t = useT('admin_ui');
  const { id } = useParams<{ id: string }>();
  const view = useTerminalConfig(id);
  const update = useUpdateTerminal();
  // Only for naming the till in the embedded parameter editor; the config
  // view itself does not carry the code. Falls back to the id.
  const estate = useAllTerminals();
  const terminalLabel = (() => {
    const terminal = estate.data?.find((candidate) => candidate.id === id);
    return terminal === undefined ? (id ?? '') : `${terminal.code} · ${terminal.displayName}`;
  })();
  const canWrite = useHasPermission('terminal-management.write');
  const [draft, setDraft] = useState<TerminalConfigOverrideValue | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (view.isLoading)
    return <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</p>;
  if (view.data === undefined) {
    return (
      <p className="text-sm text-destructive">
        {t('terminal.hardware.notFound', 'Terminal not found.')}
      </p>
    );
  }
  const { profile, profileConfig, effective } = view.data;
  const override = draft ?? view.data.override;
  const dirty = draft !== null;

  const setState = (ns: ConfigNamespace, next: OverrideState) => {
    const copy: TerminalConfigOverrideValue = { ...override };
    if (next === 'inherited') delete copy[ns];
    else if (next === 'suppressed') copy[ns] = null;
    else {
      // Seed an override from whatever is currently effective, so switching to
      // "override" starts from what the till has today rather than from blank
      // — an operator overriding one field should not have to retype the rest.
      const seed = (effective[ns] ?? NAMESPACE_DEFAULTS[ns]) as never;
      copy[ns] = seed;
    }
    setDraft(copy);
  };

  return (
    <div className="space-y-5">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">
            {t('terminals.detail.title', 'Terminal configuration')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {profile === null ? (
              <>
                {t(
                  'terminals.detail.noProfile',
                  'This terminal is not bound to a profile, so there is nothing to inherit — every namespace below is either set here or absent.',
                )}
              </>
            ) : (
              <>
                {t('terminals.detail.inheritingFrom', 'Inheriting from profile')}{' '}
                <Link className="underline" to="/terminal-profiles">
                  {profile.name}
                </Link>
                {t(
                  'terminals.detail.inheritanceHelp',
                  ". A per-terminal value wins over the profile's.",
                )}
              </>
            )}
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 text-sm">
          {/* RFL-238 — hardware is authored separately from the config bag, on
              its own version token, so the two never invalidate each other. */}
          <Link className="underline" to={`/terminals/${id ?? ''}/hardware`}>
            {t('terminals.detail.hardwareLink', 'Hardware (printer, scanner, fiscal)')}
          </Link>
          <Link className="underline" to="/terminals">
            {t('terminals.backToTerminals', 'Back to terminals')}
          </Link>
        </div>
      </header>

      {error !== null && (
        <p className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {error}
        </p>
      )}

      <div className="space-y-4">
        {CONFIG_NAMESPACES.map((ns) => {
          const state = stateOf(override, ns);
          const value = override[ns];
          return (
            <section key={ns} className="rounded-md border bg-card p-5">
              <div className="mb-3 flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-sm font-medium">{namespaceLabel(ns, t)}</h2>
                  <p className="text-xs text-muted-foreground">
                    {t('terminals.field.profile', 'Profile')}:{' '}
                    {summariseNamespace(ns, profileConfig[ns], t)} ·{' '}
                    {t('terminals.detail.deliveredToTill', 'Delivered to this till')}:{' '}
                    <span className="font-medium text-foreground">
                      {summariseNamespace(ns, effective[ns], t)}
                    </span>
                  </p>
                </div>
                <select
                  aria-label={t('terminals.detail.sourceAria', '{namespace} source').replace(
                    '{namespace}',
                    namespaceLabel(ns, t),
                  )}
                  className="h-9 rounded-md border bg-background px-2 text-sm"
                  value={state}
                  disabled={!canWrite}
                  onChange={(e) => setState(ns, e.target.value as OverrideState)}
                >
                  <option value="inherited">
                    {t('terminal.hardware.state.inherited', 'Inherit')}
                  </option>
                  <option value="overridden">{t('terminals.detail.override', 'Override')}</option>
                  <option value="suppressed">{t('terminals.detail.suppress', 'Suppress')}</option>
                </select>
              </div>
              <p className="mb-3 text-xs text-muted-foreground">
                {t(STATE_KEY[state], STATE_LABEL[state])}
              </p>
              {state === 'overridden' && value !== null && value !== undefined && (
                <NamespaceFields
                  namespace={ns}
                  value={value}
                  onChange={(next) => setDraft({ ...override, [ns]: next })}
                  disabled={!canWrite}
                />
              )}
            </section>
          );
        })}
      </div>

      {/* RL-279 — the same parameter editor as Settings → Parameters, pinned to
          this terminal (TP-FR-009). Embedded rather than given a route or a
          menu entry, like the peripherals editor (TP-FR-010). */}
      {id !== undefined && <KitchenHealthCard terminalId={id} />}

      {/* SSC-260 — renders only for an SCO lane that reports its SecureScale weight copy. */}
      {id !== undefined && <SecureScaleCopyCard terminalId={id} />}

      {/* EAT-25 — renders only for a self-checkout lane that reports held fiscal sales. */}
      {id !== undefined && <FiscalHoldPanel terminalId={id} />}

      {/* MICRO-46 — renders only for a lane that reports card end of day. */}
      {id !== undefined && <CardEodPanel terminalId={id} />}

      {/* PFC-10 — renders only for a fiscal-cabinet tenant and a user with fiscal.cabinet.write. */}
      {id !== undefined && <TerminalPrroPanel terminalId={id} />}

      {id !== undefined && (
        <section className="rounded-md border bg-card p-5">
          <h2 className="mb-2 text-sm font-medium">
            {t('terminals.detail.parameters', 'Parameters')}
          </h2>
          <PinnedParameters
            target={{
              scope: 'terminal',
              terminalId: id,
              terminalLabel,
              profileId: profile?.id ?? null,
              profileName: profile?.name ?? null,
            }}
          />
        </section>
      )}

      {canWrite ? (
        <div className="flex items-center gap-3">
          <Button
            disabled={!dirty || update.isPending || id === undefined}
            onClick={async () => {
              if (id === undefined) return;
              setError(null);
              try {
                // An override with no namespaces is sent as null rather than {}:
                // "this terminal overrides nothing" is the absence of a bag, and
                // storing an empty object would leave a row that reads as
                // configured to anyone looking at the database.
                const body = Object.keys(override).length === 0 ? null : override;
                await update.mutateAsync({
                  id,
                  body: {
                    configOverride: body,
                    expectedConfigVersion: view.data.configVersion,
                  },
                });
                setDraft(null);
              } catch (err) {
                setError(extractError(err, t));
              }
            }}
          >
            {update.isPending
              ? t('common.saving', 'Saving…')
              : t('terminals.detail.save', 'Save override')}
          </Button>
          {dirty && (
            <Button variant="ghost" onClick={() => setDraft(null)}>
              {t('common.discardChanges', 'Discard changes')}
            </Button>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          {t('terminals.detail.readOnly', 'You have read-only access to terminal configuration.')}
        </p>
      )}
    </div>
  );
}

/**
 * Surface the server's validation message verbatim. The strict config schema
 * is enforced on write precisely so a typo is refused while the person who
 * made it is still looking at it — swallowing that message and showing
 * "invalid configuration" would throw away the only part that says which field.
 */
function extractError(err: unknown, t: (key: string, bakedEnglish?: string) => string): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as { message?: string | string[] } | undefined;
    const message = data?.message;
    if (Array.isArray(message)) return message.join('; ');
    if (typeof message === 'string') return message;
  }
  return t('terminals.detail.saveFailed', 'Could not save the override.');
}

export function terminalConfigStateForTest(
  override: TerminalConfigOverrideValue,
  ns: ConfigNamespace,
): OverrideState {
  return stateOf(override, ns);
}

export type { TerminalConfigValue };
